/**
 * This module manages the calendarItems model of the API
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 * @author DJ Kim <dj@thebrandingfarm.com>
 */
import isEmpty from 'lodash/isEmpty'
import cloneDeep from 'lodash/cloneDeep'
import dayjs from 'dayjs'

import api from '@/api'
import router from '@/router'

import {
    // CALENDAR ITEMS
    VUEX_CALENDAR_ITEMS_GET_REQUEST,
    VUEX_CALENDAR_ITEMS_GET_SUCCESS,
    VUEX_CALENDAR_ITEMS_GET_FAILURE,
    VUEX_CALENDAR_ITEMS_APPLY_FILTERS,
    VUEX_CALENDAR_ITEMS_CLEAR_FILTERS,
    VUEX_CALENDAR_ITEMS_APPLY_STATUS_FILTER,
    VUEX_CALENDAR_ITEMS_APPLY_PROGRESS_FILTER,
    VUEX_CALENDAR_ITEMS_APPLY_TYPE_FILTER,
    VUEX_CALENDAR_ITEMS_APPLY_DEPARTMENT_FILTER,
    VUEX_CALENDAR_ITEMS_APPLY_CREATIVE_ROUND_FILTER,
    VUEX_CALENDAR_ITEMS_SYNC_PAGINATION,
    VUEX_CALENDAR_ITEMS_SYNC_PAGINATION_SORT,
    VUEX_CALENDAR_ITEMS_SET_APPROVED,
    VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST,
    VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST_SUCCESS,
    VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST_FAILURE,
    VUEX_CALENDAR_ITEMS_ENTER_FROM_NO_RELOAD,

    // CALENDAR ITEM
    VUEX_CALENDAR_ITEM_GET_REQUEST,
    VUEX_CALENDAR_ITEM_GET_SUCCESS,
    VUEX_CALENDAR_ITEM_GET_EVENTS_SUCCESS,
    VUEX_CALENDAR_ITEM_GET_FILES_SUCCESS,
    VUEX_CALENDAR_ITEM_UPDATE_PROP,
    VUEX_CALENDAR_ITEM_UPDATE_LATE_ARRIVAL,
    VUEX_CALENDAR_ITEM_SET_LATE_ARRIVAL_ID,
    VUEX_CALENDAR_ITEM_UPDATE_APPLY_TEMPLATE,
    VUEX_CALENDAR_ITEM_UPDATE_REQUEST,
    VUEX_CALENDAR_ITEM_UPDATE_REQUEST_SUCCESS,
    VUEX_CALENDAR_ITEM_UPDATE_REQUEST_FAILURE,
    VUEX_CALENDAR_OVERLAP_GET_REQUEST,
    VUEX_CALENDAR_OVERLAP_GET_SUCCESS,


    // CREATE CALENDAR ITEM
    VUEX_CALENDAR_ITEM_CREATE_INIT,
    VUEX_CALENDAR_ITEM_CREATE,
    VUEX_CALENDAR_ITEM_CREATE_APPLY_TEMPLATE,
    VUEX_CALENDAR_ITEM_CREATE_SUCCESS,
    VUEX_CALENDAR_ITEM_CREATE_FAILURE,
    VUEX_CALENDAR_ITEM_CREATE_UPDATE,
    VUEX_CALENDAR_ITEM_CREATE_UPDATE_SUCCESS,
    VUEX_CALENDAR_ITEM_CREATE_UPDATE_FAILURE,
    VUEX_CALENDAR_ITEM_CREATE_CANCEL,
    VUEX_CALENDAR_ITEM_CREATE_CANCEL_PROMPT,
    VUEX_CALENDAR_ITEM_CREATE_CLEAR_PROPS_TO_UPDATE,
    VUEX_CALENDAR_ITEM_CREATE_UPDATE_PROP,
    VUEX_CALENDAR_ITEM_CREATE_HAS_CHANGED,

    VUEX_CALENDAR_ITEM_REMOVE,
    VUEX_CALENDAR_ITEM_REMOVE_SUCCESS,
    VUEX_CALENDAR_ITEM_REMOVE_FAILURE,
    
    VUEX_CALENDAR_ITEM_CHANGE_TYPE,

    // ROUTE EXIT
    VUEX_CALENDAR_ABSENCES_EXIT,


    VUEX_CALENDAR_ITEMS_SET_FILTER_TOGGLER,
    VUEX_CALENDAR_ITEMS_STORE_QUICK_FILTER,

    VUEX_CALENDAR_ATTACH_FILE,
    VUEX_CALENDAR_ATTACH_FILE_SUCCESS,
    /*VUEX_CALENDAR_ABSENCE_ATTACH_FILE,
    VUEX_CALENDAR_ABSENCE_ATTACH_FILE_SUCCESS,*/
    VUEX_CALENDAR_ITEM_GET_ATTACHMENTS,
    VUEX_CALENDAR_ITEM_GET_ATTACHMENTS_SUCCESS,
    VUEX_CALENDAR_ITEM_GET_ATTACHMENTS_FAILURE,
    VUEX_CALENDAR_FILE_UPLOAD_SUCCESS,
    VUEX_CALENDAR_FILE_UPLOAD_TRANSITION_ITEM,
    VUEX_CALENDAR_HANDLE_FILE_NOTIFICATION,


    //ABSENCES RELATED
    VUEX_ABSENCES_APPLY_DEEPSTATE,
    VUEX_ABSENCES_APPLY_QUICK_FILTER,
    VUEX_ABSENCES_APPLY_STAFF_FILTER,
    VUEX_ABSENCES_APPLY_STATUS_FILTER,
    VUEX_ABSENCES_APPLY_TYPE_FILTER,
    VUEX_ABSENCES_APPLY_USER_STAFF,
    VUEX_ABSENCES_APPLY_USER_FREELANCE,

    VUEX_ABSENCES_SET_SIDEBAR_MAINTAINER,
    VUEX_ABSENCES_HANDLE_ROW_CLICK
} from '@/store/constants/calendarItems'
import {
    VUEX_ROUTING_EXIT_CALENDAR_ITEM
} from '@/store/constants/routing'
import { 
    VUEX_UI_HIDE_RIGHT_DRAWER
} from '@/store/constants/ui'
import { 
    VUEX_SNACKBAR_SHOW
} from '@/store/constants/snackbar'
import { 
    VUEX_AUTH_LOGOUT
} from '@/store/constants/auth'
import {
    VUEX_PERSISTENCE_SET_VALUE
} from '@/store/constants/persistence'



/**
 * An empty representation of a calendarItem object model needed for new calendarItem creation
 * @const
 * @todo This will become dynamically registered vuex module when the creation route loads
 */

let currentDate = (pos) => {

    let tzoneOffset = (new Date()).getTimezoneOffset() * 60000
    let date = new Date(Date.now() - tzoneOffset).toISOString()

    let hour = pos === 'start' ? +date.slice(11, 13) + 1 : +date.slice(11, 13) + 2

    hour = String("0" + hour).slice(-2);

    return date.slice(0, 10) + ' ' + hour + ':00:00'
}

let getDateStart = () => {
    return currentDate()
}

let getDateEnd = () => {
    return currentDate()
}

const createCalendarItem_Template = {
    name:              null,
    content:           null,
    note:              null,
    type_id:           8,
    all_day:           false,
    date_start:        currentDate('start'),
    date_end:          currentDate('end'),
    transition:        'submit',
    files:             []
}

const updateCalendarItem_Template = {
    name:              null,
    content:           null,
    note:              null,
    type_id:           8,
    all_day:           false,
    date_start:        null,
    date_end:          null,
    files: [
        // FILES
    ]
}


/**
* CalendarItems module state
* @const
*/
const state = {
    calendarItems: [], //used for the view
    calendarItemsLinks: {},
    calendarItemsLoaded: false,
    calendarItemsApproved: true,

    calendarItem: {},
    calendarItemAttachments: [],
    calendarItemLinks: {},
    calendarItemOverlap: {},
    calendarItemEvents: {},
    calendarItemEventsLinks: {},
    calendarItemFiles: {},
    lateArrivalItemId: '',
    calendarItemLoaded: false,
    restorePreviousCalendarItemList: false,
    appliedFilters: [],
    pagination: {
        rowsPerPage: 10,
        totalItems: 10,
        sortBy: 'updated_at',
        descending: true,
        page: 1
    },

    updateCalendarItem_Data: {},
    createCalendarItem_Data: {},
    createCalendarItem_PropsToUpdate: {},
    createCalendarItem_Saved: false,
    createCalendarItem_IsSaving: false,
    isEdit: false,
    loading: false,
    isAbsence: true,

    createCalendarItem_CancelPrompt: false,


    // SPECIAL FILTERS
    filterToggler: '',
    currentQuickFilter: {},

    //absenses & time off filter related
    absencesMeta:[],

    //TOAN sidebar maintainer
    toanSidebarMaintainer: {},
}



/**
* CalendarItems module getters
* @const
*/
const getters = {
    /**
    * This getter returns the events attached to the currently loaded calendarItem
    * @function
    * @param {state} state
    */
    getCalendarItemEvents: state => {
        if (state.calendarItemLoaded)
            return state.calendarItem.events
        return []
    },


    /**
    * This getter returns the files attached to the currently loaded calendarItem
    * @function
    * @param {state} state
    */
    getCalendarItemFiles: state => {
        if (state.calendarItemLoaded)
            return state.calendarItemFiles
        return []
    },


    /**
    * This getter returns the team members attached to the currently loaded calendarItem
    * @function
    * @param {state} state
    */
    getCalendarItemTeam: state => {
        if (state.calendarItemLoaded)
            return state.calendarItem.team
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded calendarItem
    * @function
    * @param {state} state
    */
    getCalendarItemSwapRows: state => {
        if (state.calendarItemLoaded)
            return state.calendarItem.swapRows
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded calendarItem
    * @function
    * @param {state} state
    */
    getCalendarItemId: state => {
        if (state.calendarItemLoaded)
            return state.calendarItem.id
        return null
    },


    /**
    * This getter returns the swaps attached to the currently loaded calendarItem
    * @function
    * @param {state} state
    */
    getCreateCalendarItemData: state => {
        return state.createCalendarItem_Data
    },


    /**
     *
     * @param state
     * @returns {function(*): string}
     */
    getAbsencesFilterCount: (state) => (obj) => {
        var id = obj.id;
        var ret = '';

        if (state.absencesMeta &&
            state.absencesMeta.hasOwnProperty('filter_counts') &&
            state.absencesMeta.filter_counts.hasOwnProperty(id) )
        {
            ret = state.absencesMeta.filter_counts.hasOwnProperty(id) ? state.absencesMeta.filter_counts[id] : ret;
        }
        return String(ret);
    },

}




/**
 * CalendarItems module action definitions
 * @const
 */
const actions = {
    [VUEX_CALENDAR_ABSENCES_EXIT]: async ({ state, commit, dispatch }, key) => {
        //!!PERSISTENCE
        var key = 'toan'
        var dsobj = {}
        dsobj.currentQuickFilter = cloneDeep(state.currentQuickFilter)
        dsobj.appliedFilters = cloneDeep(state.appliedFilters)
        dsobj.pagination = cloneDeep(state.pagination)
        var obj = {
            key: key,
            value: dsobj
        }
        await dispatch(VUEX_PERSISTENCE_SET_VALUE, obj)

        //NOW LEAVE
        await commit(VUEX_CALENDAR_ABSENCES_EXIT)

    },



    /**
    * Get all calendarItems
    * Params :
        approved: (approved calendarItems | calendarItem requests)
        ...table pagination (sorting, page, itemsPerRow, etc)
        filters
    * @function
    */
    /* TODO: needs error handling */


    [VUEX_CALENDAR_ITEM_CHANGE_TYPE]: async ({ state, commit }, isAbsence) => {
        commit(VUEX_CALENDAR_ITEM_CHANGE_TYPE, isAbsence)
    },

    [VUEX_CALENDAR_ITEMS_GET_REQUEST]: async ({ state, commit }) => {
        commit(VUEX_CALENDAR_ITEMS_GET_REQUEST)

        let params = Object.assign(
            {
                approved: state.calendarItemsApproved,
                page: state.pagination.page,
                per_page: state.pagination.rowsPerPage,
            },
        )
        if (state.pagination && state.pagination.sortBy) {
            params['sort'] = state.pagination.descending ? '-' + state.pagination.sortBy : state.pagination.sortBy
        }

        for (var f in state.appliedFilters) {
            var key = state.appliedFilters[f]['filterOn']
            var val = state.appliedFilters[f]['value']
            if (!params.hasOwnProperty(key)) {
                params[key] = val
            } else {
                params[key] = params[key] + ',' + val
            }
        }

        params['include'] = 'avatar'

        await api.get('/attendance', { params }).then(async (response) => {
            await commit(VUEX_CALENDAR_ITEMS_GET_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEMS_GET_FAILURE, err)
        })
    },

    [VUEX_ABSENCES_APPLY_DEEPSTATE]: async({getters, commit, dispatch, rootState}, dsobj) => {
        await commit(VUEX_ABSENCES_APPLY_DEEPSTATE, dsobj)
        await dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST, {})
    },

    [VUEX_CALENDAR_ITEMS_CLEAR_FILTERS]: async ({ commit }) => {
        await commit(VUEX_CALENDAR_ITEMS_CLEAR_FILTERS)
    },

    [VUEX_CALENDAR_ITEMS_APPLY_FILTERS]: async ({ commit, dispatch }, filterItem) => {
        await commit(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, filterItem)
        await dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
    },

    [VUEX_CALENDAR_ITEMS_APPLY_STATUS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'status'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_CALENDAR_ITEMS_APPLY_PROGRESS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'progress'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_CALENDAR_ITEMS_APPLY_TYPE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'type'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_CALENDAR_ITEMS_APPLY_DEPARTMENT_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'department'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_CALENDAR_ITEMS_APPLY_CREATIVE_ROUND_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'cretive_round'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_CALENDAR_ITEMS_SYNC_PAGINATION]: ({commit, dispatch}, pagination) => {
        //
        // TODO: BL
        // This is a hack to get around the fact that the set/get on the jobs grid calls this on
        // its load b/c the vuetify table changes pagination when it gets mounted (defaults to
        // sort by first sortable column in header). I could block it on the grid component, but
        // it seems illogical to keep syncing pagination if there's no jobs anyway.
        //
        if (state.calendarItemsLoaded === false) {
            return
        }

        //kill quick filter display because then clicked
        dispatch(VUEX_CALENDAR_ITEMS_SET_FILTER_TOGGLER, { value: "manual"})

        commit(VUEX_CALENDAR_ITEMS_SYNC_PAGINATION, pagination)
        dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
    },


     /**
    * Get calendarItems By Date Range
    * Params :
        startDate
        endDate
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST]: async ({ commit }, payload={}) => {
        commit(VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST);
        var startDate = payload.startDate || dayjs().startOf('month')
        var endDate = payload.endDate || dayjs().endOf('month')

        //add a month before and a month after padding for dates that wrap between months
        const DATE_FORMAT = 'YYYY-MM-DD hh:mm:ss'
        startDate = dayjs(startDate).subtract(1, 'month').format(DATE_FORMAT)
        endDate = dayjs(endDate).add(1, 'month').format(DATE_FORMAT)

        const url = `calendar_events?m=1&date_start=${startDate}&range_end=${endDate}`
        await api.get(url).then((response) => {
            commit(VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST_FAILURE)
            console.error(err)
        })
    },

    /**
    * Get calendarItem -> get events -> get files
    * Params :
        calendarItem id
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_CALENDAR_ITEM_GET_REQUEST]: async ({ dispatch, commit }, payload) => {
        commit(VUEX_CALENDAR_ITEM_GET_REQUEST)

        if (typeof payload === 'object') {
            // Is calendarItem object
            let post_url = payload.entity === 'AbsenceNotice' ? '/absence_notices' : '/time_off_requests'
            post_url += '/' + payload.id
            
            await api.get(post_url).then((response) => {
                commit(VUEX_CALENDAR_ITEM_GET_SUCCESS, response.data)
            })
        } else if (typeof payload == 'string') {
            // Is calendarItem ID
            // TODO: this should be a since API route which can search both BE tables
            await api.get(`/time_off_requests/${payload}`).then(async (response) => {
                await dispatch(VUEX_CALENDAR_ITEM_GET_ATTACHMENTS, { item:payload, type:'time_off_requests' })
                await commit(VUEX_CALENDAR_ITEM_GET_SUCCESS, response.data)
                dispatch(VUEX_CALENDAR_OVERLAP_GET_REQUEST, response.data.data)
            }).catch(async () => {
                await api.get(`/absence_notices/${payload}`).then(async (response) => {
                    await dispatch(VUEX_CALENDAR_ITEM_GET_ATTACHMENTS, { item:payload, type:'absence_notices' })
                    await commit(VUEX_CALENDAR_ITEM_GET_SUCCESS, response.data)
                })
            })
        }
    },


    /**
    * Get calendarItemOverlap
    * Params :
        calendarItem
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_CALENDAR_OVERLAP_GET_REQUEST]: async ({ state, dispatch, commit }, calendarItem) => {
        commit(VUEX_CALENDAR_ITEM_GET_REQUEST);

        let post_url = calendarItem.entity === 'AbsenceNotice' ? '/absence_notices' : '/time_off_requests'
        post_url += '/overlap'

        let params = {
            'date_start': calendarItem.date_start,
            'range_end': calendarItem.date_end
        }

        await api.get(post_url, { params }).then((response) => {
            commit(VUEX_CALENDAR_OVERLAP_GET_SUCCESS, response)
        })
    },

    /**
    * Update the current calendarItem's properties
    * @function
    */
    [VUEX_CALENDAR_ITEM_UPDATE_REQUEST]: async ({ state, dispatch, commit }, payload) => {
        let post_url = state.calendarItem.entity === 'AbsenceNotice' ? '/absence_notices' : '/time_off_requests'
        post_url += '/' + state.calendarItem.id

        await api.patch(post_url, payload).then(async (response) => {
            await commit(VUEX_CALENDAR_ITEM_UPDATE_REQUEST_SUCCESS, response)
            await dispatch(VUEX_CALENDAR_ITEM_GET_REQUEST, state.calendarItem)

            dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEM_UPDATE_REQUEST_FAILURE, err)
        })
    },

    /**
    * Update the late arrival calendarItem's properties
    * @function
    */
    [VUEX_CALENDAR_ITEM_UPDATE_LATE_ARRIVAL]: async ({ state, dispatch, commit }, payload) => {
        payload = payload.name
        await api.patch(`/absence_notices/${payload.lateArrivalId}`, { transition:payload.transition }).then((response) => {
            commit(VUEX_CALENDAR_ITEM_UPDATE_REQUEST_SUCCESS, response)
            dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEM_UPDATE_REQUEST_FAILURE, err)
        })
    },

    /**
     * Update the current calendarItem's name
     * @function
     */
    [VUEX_CALENDAR_ITEM_UPDATE_PROP]: ({dispatch}, params) => {
        dispatch(VUEX_CALENDAR_ITEM_UPDATE_REQUEST, params)

        // Do something on a transition
        if(params.hasOwnProperty('transition')) {
            switch(params.transition) {
                // cancel transition
                case 'cancel' :
                    // TODO: close mobile or decktop drawer
                    dispatch(VUEX_UI_HIDE_RIGHT_DRAWER)
                    if(params.hasOwnProperty('fromGlobalActionCancel')) {
                        dispatch(VUEX_CALENDAR_ITEM_GET_REQUEST,params.fromGlobalActionCancel)
                    }
                    break
            }
        }
    },


    /**
     * Create CalendarItem Request
     * @function
     */
    [VUEX_CALENDAR_ITEM_CREATE_INIT]: ({ state, commit, dispatch }, opts) => {
        commit(VUEX_CALENDAR_ITEM_CREATE_APPLY_TEMPLATE, opts)
        commit(VUEX_CALENDAR_ITEM_CREATE_INIT)

        //GLOBAL ACTION PROMPT: Pass array of optional states to watch
        //pass state string names to address nasty issue of vuex not detecting "some" mutations across components
        var stateChangeWatchers = [
            ['calendarItems','createCalendarItem_Data']
        ]
        dispatch('VUEX_GLOBAL_ACTION_PROMPT_SET_STATE_CHANGE_WATCHERS', { stateChangeWatchers: stateChangeWatchers})
    },

    [VUEX_CALENDAR_ITEM_CREATE]: async ({ state, commit, dispatch }, payload) => {
        let post_url
        commit(VUEX_CALENDAR_ITEM_CREATE)
        commit(VUEX_CALENDAR_ITEM_CREATE_UPDATE_PROP, payload)

        post_url = state.isAbsence ? '/absence_notices' : '/time_off_requests'

        await api.post(post_url, state.createCalendarItem_Data).then( async(response) => {
            await commit(VUEX_CALENDAR_ITEM_CREATE_SUCCESS, response.data.data)
            await commit(VUEX_CALENDAR_ITEM_CREATE_CLEAR_PROPS_TO_UPDATE)
            await dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
            await commit(VUEX_CALENDAR_ITEM_CREATE_CANCEL)


            // Show snackbar for late arrival
            if (response.data.data.type_id === 8) {
                await commit(VUEX_CALENDAR_ITEM_SET_LATE_ARRIVAL_ID, response.data.data.id)
                var obj = {
                    lateArrivalId: response.data.data.id,
                    transition: 'confirm'
                }
                dispatch(VUEX_SNACKBAR_SHOW, {
                    description_text: "Late Arrival - Pending Confirm",
                    button_text: "I'm here",
                    timeout: 0,
                    button_dispatch: "VUEX_CALENDAR_ITEM_UPDATE_LATE_ARRIVAL",
                    button_dispatch_payload: obj,
                })
            } else {
                //Show snackbar for success
                dispatch(VUEX_SNACKBAR_SHOW, {
                    description_text: "Success! Your request has been submitted.",
                    button_text: "",
                    timeout: 4000,
                    button_dispatch: "",
                    button_dispatch_payload: {},
                })
            }
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEM_CREATE_FAILURE)
            console.error(err)
        })

        // TODO: Move this to dispatch in routing
        // TODO: Diff destination based on role
        // router.push({ name: 'calendarItem' })
    },

    [VUEX_CALENDAR_ITEM_CREATE_UPDATE]: async ({ state, commit, dispatch }, calendarItem) => {
        // commit(VUEX_CALENDAR_ITEM_CREATE_UPDATE)
        let post_url

        post_url = calendarItem.entity === "AbsenceNotice" ? '/absence_notices' : '/time_off_requests'
        post_url += '/' + calendarItem.id

        await api.patch(post_url, state.updateCalendarItem_Data).then((response) => {
            commit(VUEX_CALENDAR_ITEM_CREATE_UPDATE_SUCCESS, response.data.data)
            dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
            commit(VUEX_CALENDAR_ITEM_CREATE_CLEAR_PROPS_TO_UPDATE)
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEM_CREATE_UPDATE_FAILURE)
            console.error(err)
        })
    },

    [VUEX_CALENDAR_ITEM_CREATE_HAS_CHANGED]: ({state, commit}) => {
        if(!isEmpty(state.createCalendarItem_PropsToUpdate)) {
            commit(VUEX_CALENDAR_ITEM_CREATE_HAS_CHANGED, true)
        } else {
            commit(VUEX_CALENDAR_ITEM_CREATE_HAS_CHANGED, false)
        }
    },

    [VUEX_CALENDAR_ITEM_CREATE_CANCEL]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_CALENDAR_ITEM_CREATE_CANCEL_PROMPT, false)
        commit(VUEX_CALENDAR_ITEM_CREATE_CANCEL)
    },

    [VUEX_CALENDAR_ITEM_CREATE_UPDATE_PROP]: ({ commit, dispatch }, payload) => {
        commit(VUEX_CALENDAR_ITEM_CREATE_UPDATE_PROP, payload)
    },

    [VUEX_CALENDAR_ITEM_REMOVE]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_CALENDAR_ITEM_REMOVE)

        await api.delete(`/calendar_items/${state.calendarItem.id}`).then((response) => {
            commit(VUEX_CALENDAR_ITEM_REMOVE_SUCCESS, response.data.data)
            dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST)
            commit(VUEX_CALENDAR_ITEM_CREATE_CANCEL)
        }).catch((err) => {
            commit(VUEX_CALENDAR_ITEM_REMOVE_FAILURE)
            console.error(err)
        })
    },




    /**
     * THIS FILTER TOGGLER IS USED TO SYNC UP BUTTON DISPLAY STATES
     */
    [VUEX_CALENDAR_ITEMS_SET_FILTER_TOGGLER]:({ commit, state }, obj) => {
        var oldValue = state.filterToggler
        var newValue = obj.value

        // always refresh quick
        if (newValue !== 'manual') {
            newValue = 'quick' + Date.now()
        }

        //clear out the quick filter in storage
        if (newValue === 'manual') {
            commit(VUEX_CALENDAR_ITEMS_STORE_QUICK_FILTER, {})
        }

        if (oldValue !== 'manual' && newValue === 'manual') {
            //Probably don't do this anymore now that we have me button, but may add it again
            //commit(VUEX_JOBS_REMOVE_APPLIED_FILTER_BY_NAME, "userMeInRole")
        }

        commit(VUEX_CALENDAR_ITEMS_SET_FILTER_TOGGLER, newValue)
    },


    ////////////////////////////////////////////////////////////////
    //TIME OFF FILTERS
    ////////////////////////////////////////////////////////////////
    [VUEX_ABSENCES_APPLY_STAFF_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'staff'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_ABSENCES_APPLY_STATUS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'status'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_ABSENCES_APPLY_TYPE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'type'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_ABSENCES_APPLY_USER_STAFF]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'userStaff'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_ABSENCES_APPLY_USER_FREELANCE]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'userFreelance'
        await dispatch(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
    },


    [VUEX_ABSENCES_APPLY_QUICK_FILTER]: async({getters,commit, dispatch, rootState}, quickFilterConfig) => {
        //let extraParams = cloneDeep(quickFilterConfig) - probably not going to pass these
        let extraParams = {}
        let newFilterItem = {}

        let userMeInRoleObject = getters.getUserMeInRoleObject;
        switch (quickFilterConfig.value) {
            case 1:
                //Pending Approval
                //Status: Pending Approval 7, and Resubmitted 9
                newFilterItem = {}
                newFilterItem.filterOn = 'status'
                newFilterItem.value = 7
                await commit(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)

                //newFilterItem = {}
                //newFilterItem.filterOn = 'status'
                //newFilterItem.value = 9
                //await commit(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)


                break;
            case 2:
                //Pending Print Approval
                //Status:  Pending HR Submission and Pending Kronos Submission Screenshot Confirmation
                newFilterItem = {}
                newFilterItem.filterOn = 'status'
                newFilterItem.value = 13
                await commit(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)

                newFilterItem = {}
                newFilterItem.filterOn = 'status'
                newFilterItem.value = 16
                await commit(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)

                break;
            case 3:
                //In Production
                //Status: All

                for (var i = 7;i<=18;i++) {
                    if (i !== 9 ) { //ignore resubmit 9
                        newFilterItem = {}
                        newFilterItem.filterOn = 'status'
                        newFilterItem.value = i
                        await commit(VUEX_CALENDAR_ITEMS_APPLY_FILTERS, newFilterItem)
                    }
                }

                break;

        }

        // Set our sorting
        await commit(VUEX_CALENDAR_ITEMS_SYNC_PAGINATION_SORT, quickFilterConfig.sort)

        // commit this quick filter to memory
        await commit(VUEX_CALENDAR_ITEMS_STORE_QUICK_FILTER, quickFilterConfig)

        // Go go go!!!
        await dispatch(VUEX_CALENDAR_ITEMS_GET_REQUEST, extraParams)
    },


    /**
     * File Attachment
     */
    // TODO: Come back and, you know, make this do the right thing
    [VUEX_CALENDAR_ATTACH_FILE]: async ({ dispatch, commit }, payload) => {
        // DIRECTLY ADD ATTACHMMENT TO ATTACHED ITEMS
        // THIS ALLOWS THE USER UPLOADING ATTACHMENT TO SEE ATTACHMENT WITHOUT CALLING GET_ITEM AGAIN
        await commit(VUEX_CALENDAR_ATTACH_FILE_SUCCESS, payload)
        api.post(`/attachments`, {
            file_id: payload.id,
            model_id: payload.attach_to.model_id,
            model: payload.attach_to.model,
            note: payload.note || null,
        }).then(async (response) => {
            // GET CURRENT ITEM AGAIN (TO GET ATTACHMENTS)
            // await dispatch(VUEX_CALENDAR_ITEM_GET_REQUEST, payload.attach_to.model_id)
        }).catch((err) => {
            console.error(err)
        })
    },

    [VUEX_CALENDAR_ITEM_GET_ATTACHMENTS]: async ({ commit, state }, payload) => {
        await api.get(`/${payload.type}/${payload.item}/attachments`).then(async (response) => {
            await commit(VUEX_CALENDAR_ITEM_GET_ATTACHMENTS_SUCCESS, response.data.data)
        }).catch((err) => {
            console.error(err)
        })
    },

    [VUEX_CALENDAR_FILE_UPLOAD_SUCCESS]: async({state, dispatch, commit}, payload) => {
        dispatch(VUEX_CALENDAR_FILE_UPLOAD_TRANSITION_ITEM, payload)
        // Add other actions based on file upload success
    },

    [VUEX_CALENDAR_FILE_UPLOAD_TRANSITION_ITEM]: async({ dispatch, commit }, payload) => {
        // get item id of completed file
        let item_id = payload.attach_to.model_id
        // construct api route | based on model and model_id
        let api_route = `/${payload.attach_to.model === SILA_FILE_ATTACHMENT_MODEL_ABSENCE_NOTICE ? 'absence_notices' : 'time_off_requests'}/` + item_id

        // get item
        api.get(api_route).then(async(getRes) => {
            let transition

            // check state and assign next transition
            switch(getRes.data.data.state) {
                case SILA_ABSENCE_NOTICE_STATE_SKECHFORCE_SCREENSHOT_PENDING :
                case SILA_TIME_OFF_REQUEST_STATE_SKECHFORCE_SCREENSHOT_PENDING :
                    transition = 'skechforce_upload' 
                    break

                default : 
                    transition = null
                    break
            }

            // if transition defined, make patch on item
            if (transition) {
                item_id == getRes.data.data.id

                api.patch(api_route, { transition: transition }).then(async(patchRes) => {

                }).catch((err) => {
                    console.error(err)
                })
            }
        }) 
    },

    [VUEX_CALENDAR_HANDLE_FILE_NOTIFICATION]: async ({ state, dispatch }, payload) => {
        let match = state.calendarItemAttachments.findIndex(item => item.id === payload.model_id)
        if (match > -1) {
            setTimeout(() => {
                dispatch(VUEX_CALENDAR_ITEM_GET_REQUEST, state.calendarItem.id)
            }, 500)
        }
    },



    [VUEX_ABSENCES_HANDLE_ROW_CLICK]: async ({ state, commit, dispatch }, calendarItemObj,currentCalendarItemId) => {
        //handle row click
        let calendarItemId = calendarItemObj.id
        if (calendarItemId !== currentCalendarItemId) {
            //check to see if row was clicked while the sidebar is up
            //if side bar is up, save the row to persistence and open after route change
            //this lets you browser multiple items while the side bar is open, and also maintain route deeplink changes
            let route = router.history.current.name
            if (route=='absences-notice') {
                //in side bar route - just save state
                await dispatch(VUEX_ABSENCES_SET_SIDEBAR_MAINTAINER, calendarItemObj)

            }

            await dispatch(VUEX_CALENDAR_ITEM_GET_REQUEST, calendarItemObj)
            if (calendarItemObj.entity === 'TimeOffRequest') {
                //this.loadOverlap(calendarItemObj)
                await dispatch(VUEX_CALENDAR_OVERLAP_GET_REQUEST, calendarItemObj)
            }
            router.push({ name:'absences-notice', params:{ absenceId: calendarItemId } })

        }

    },

    [VUEX_ABSENCES_SET_SIDEBAR_MAINTAINER]: async ({ state, commit, dispatch }, payload) => {
        await commit(VUEX_ABSENCES_SET_SIDEBAR_MAINTAINER,payload)

    },

    //this is called if we are closing the side bar
    [VUEX_CALENDAR_ITEMS_ENTER_FROM_NO_RELOAD]: async ({ state, commit, dispatch }, payload) => {
        if (state.toanSidebarMaintainer) {
            await dispatch(VUEX_ABSENCES_HANDLE_ROW_CLICK,state.toanSidebarMaintainer,'')
        }
        //clear sidebar maintainer
        await commit(VUEX_ABSENCES_SET_SIDEBAR_MAINTAINER,{})
    },



}

/**
 * CalendarItems module mutation definitions
 * @const
 */
const mutations = {

    [VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST]: (state) => {
        state.loading = true
    },

    [VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST_SUCCESS]: (state, payload) => {
        state.calendarItems = payload.data
        state.loading = false
    },

    [VUEX_CALENDAR_ITEMS_DATE_RANGE_REQUEST_FAILURE]: (state) => {
        state.loading = false
    },

    [VUEX_CALENDAR_ITEM_CHANGE_TYPE]: (state, isAbsence) => {
        state.isAbsence = isAbsence
    },

    [VUEX_CALENDAR_ITEMS_GET_REQUEST]: (state) => {
        state.calendarItems = []
        state.pagination.totalItems = 0
        state.calendarItemsLoaded = false
        state.loading = true
    },


    [VUEX_CALENDAR_ITEMS_GET_SUCCESS]: (state, payload) => {
        state.calendarItems = payload.data
        state.absencesMeta = payload.meta
        state.calendarItemsLinks = payload.links
        state.calendarItemsLoaded = true
        state.pagination.totalItems = payload.meta.total
        state.loading = false
    },

    [VUEX_CALENDAR_ITEMS_GET_FAILURE]: () => {
        state.loading = false
        //
    },

    //
    // Filtering
    //
    [VUEX_CALENDAR_ITEMS_APPLY_FILTERS]: (state, filterItem) => {
        let idx = state.appliedFilters.findIndex(x => x.value === filterItem.value && x.filterOn === filterItem.filterOn)
        if (idx > -1) {
            state.appliedFilters.splice(idx, 1) // remove
        } else {
            state.appliedFilters.push(filterItem) // add
        }
    },

    [VUEX_CALENDAR_ITEMS_CLEAR_FILTERS]: (state) => {
        state.appliedFilters = []
    },


    [VUEX_CALENDAR_ITEMS_SYNC_PAGINATION]: (state, pagination) => {
        state.pagination = pagination
    },

    [VUEX_CALENDAR_ITEMS_SYNC_PAGINATION_SORT]: (state, sortValue) => {
        state.pagination.sortBy = sortValue
    },


    [VUEX_CALENDAR_ITEMS_SET_APPROVED]: (state, value) => {
        state.calendarItemsApproved = value
    },

    [VUEX_CALENDAR_ITEM_GET_REQUEST]: (state) => {
        // state.calendarItemAttachments = []
        // state.calendarItem = {} // TODO: !!!! BL: Do we need to come back to this, I dunno yet
        state.calendarItemLoaded = false
    },

    [VUEX_CALENDAR_ITEM_GET_SUCCESS]: (state, payload) => {
        state.calendarItem = payload.data
        state.calendarItemLinks = payload.links
        state.calendarItemLoaded = true
        state.createCalendarItem_Saved = true

        //updates display end on active view
        let match = state.calendarItems.filter(item => item.id == payload.data.id)
        if(match.length) {
            match[0].viewed_status = true
        }
    },

    [VUEX_CALENDAR_OVERLAP_GET_SUCCESS]: (state, payload) => {
        state.calendarItemOverlap = payload.data.data
    },

    [VUEX_CALENDAR_ITEM_GET_EVENTS_SUCCESS]: (state, payload) => {
        state.calendarItemEvents = payload.data
        state.calendarItemEventsLinks = payload.links
    },

    [VUEX_CALENDAR_ITEM_GET_FILES_SUCCESS]: (state, payload) => {
        state.calendarItemFiles = payload.data
    },

    [VUEX_CALENDAR_ITEM_UPDATE_APPLY_TEMPLATE]: (state) => {
        Object.assign(state.updateCalendarItem_Data, updateCalendarItem_Template)
    },

    [VUEX_CALENDAR_ITEM_SET_LATE_ARRIVAL_ID]: (state, payload) => {
        state.lateArrivalItemId = payload
    },

    [VUEX_CALENDAR_ITEM_UPDATE_APPLY_TEMPLATE]: (state) => {
        state.updateCalendarItem_Data = updateCalendarItem_Template
    },


    [VUEX_CALENDAR_ITEM_UPDATE_REQUEST_SUCCESS]: (rootState, payload) => {
        rootState.lastSuccess = payload
    },

    [VUEX_CALENDAR_ITEM_UPDATE_REQUEST_FAILURE]: (rootState, error) => {
        rootState.lastError = error
    },

    [VUEX_CALENDAR_ITEM_CREATE_APPLY_TEMPLATE]: (state, opts) => {
        state.createCalendarItem_Data = cloneDeep(createCalendarItem_Template)

        if (opts && opts.edit) {
            state.isEdit = true
        } else {
            state.isEdit = false
        }
    },

    [VUEX_CALENDAR_ITEM_CREATE_INIT]: (state, payload) => {
        state.createCalendarItem_CancelPromp = false
        state.calendarItem = {}
    },

    // CREATE CALENDARITEM
    [VUEX_CALENDAR_ITEM_CREATE]: (state) => {
        state.createCalendarItem_IsSaving = true
    },

    [VUEX_CALENDAR_ITEM_CREATE_SUCCESS]: (state, payload) => {
        state.createCalendarItem_Data = payload
        state.createCalendarItem_IsSaving = false
        state.createCalendarItem_Saved = true
    },

    [VUEX_CALENDAR_ITEM_CREATE_FAILURE]: (state) => {
        state.createCalendarItem_IsSaving = false
        state.createCalendarItem_Saved = false
        // TODO: report error
    },


    // UPDATE CALENDARITEM
    [VUEX_CALENDAR_ITEM_CREATE_UPDATE]: (state) => {
        state.createCalendarItem_IsSaving = true
    },

    [VUEX_CALENDAR_ITEM_CREATE_UPDATE_SUCCESS]: (state, payload) => {
        state.createCalendarItem_Data = payload
        state.createCalendarItem_IsSaving = false
    },

    [VUEX_CALENDAR_ITEM_CREATE_UPDATE_FAILURE]: (state) => {
        state.createCalendarItem_IsSaving = false
        state.createCalendarItem_Saved = false
        // TODO: report error
    },

    [VUEX_CALENDAR_ITEM_CREATE_CLEAR_PROPS_TO_UPDATE]: (state) => {
        var props = Object.getOwnPropertyNames(state.createCalendarItem_PropsToUpdate);
        for (var i = 0; i < props.length; i++) {
          delete state.createCalendarItem_PropsToUpdate[props[i]]
        }
    },


    //  UPDATE PROP IN STORE
    [VUEX_CALENDAR_ITEM_CREATE_UPDATE_PROP]: (state, payload) => {
                // TODO: can this be done better/somewhere else
        // switch( Object.keys(payload)[0] ) {

        //     case 'name' :
        //         delete state.createCalendarItem_PropsToUpdate.name
        //         break

        //     case 'content' :
        //         delete state.createCalendarItem_PropsToUpdate.content
        //         break

        //     default :
        //         break
        // }

        let mergedData = Object.assign(state.createCalendarItem_Data, payload)
        state.createCalendarItem_Data = cloneDeep(mergedData)

        Object.assign(state.createCalendarItem_PropsToUpdate, payload)
    },

    [VUEX_CALENDAR_ITEM_CREATE_HAS_CHANGED]: (state, payload) => {
        state.createCalendarItem_HasChanged = payload
    },

    // CANCEL CALENDAR_ITEM
    [VUEX_CALENDAR_ITEM_CREATE_CANCEL]: (state) => {
        state.createCalendarItem_IsSaving = false
        //state.createCalendarItem_Saved = false
    },

    [VUEX_CALENDAR_ITEM_CREATE_CANCEL_PROMPT]: (state, payload) => {
        state.createCalendarItem_CancelPrompt = payload
    },

    [VUEX_CALENDAR_ITEM_REMOVE]: (state, payload) => {
    },


    [VUEX_CALENDAR_ITEM_REMOVE_SUCCESS]: (state, payload) => {
        state.createCalendarItem_Data = payload
        state.createCalendarItem_IsSaving = false
    },

    [VUEX_CALENDAR_ITEM_REMOVE_FAILURE]: (state, payload) => {
        state.createCalendarItem_IsSaving = false
        state.createCalendarItem_Saved = false
        // TODO: report error
    },

    //
    // AUTH EVENTS
    //
    [VUEX_AUTH_LOGOUT]: (state) => {
        state.calendarItems = []
        state.calendarItemsLoaded = false
        state.calendarItem = {}
        state.calendarItemLoaded = false
    },

    //
    // ROUTING EVENTS
    //
    [VUEX_ROUTING_EXIT_CALENDAR_ITEM]: (state) => {
        state.restorePreviousCalendarItemList = true
        state.calendarItemAttachments = []
    },

    [VUEX_CALENDAR_ABSENCES_EXIT]: (state) => {
        /*
        NOTE: From Matt - We shouldn't clear this, because the absence notice deep links will redraw
        The new deep link strategy handles this fine
        state.calendarItems = []
        state.appliedFilters = []
         */
        state.calendarItemsLoaded = false

        state.pagination.page = 1
        state.pagination.sortBy = 'created_at'
        state.pagination.descending = true

        state.calendarItem = {}
    },



    // Sets the watched property that will toggle filter state
    [VUEX_CALENDAR_ITEMS_SET_FILTER_TOGGLER]: (state, payload) => {
        state.filterToggler = payload
    },
    // stores the current quick filter that is applied
    [VUEX_CALENDAR_ITEMS_STORE_QUICK_FILTER]: (state, payload) => {
        state.currentQuickFilter = payload
    },



    [VUEX_ABSENCES_APPLY_DEEPSTATE]: (state, payload) => {
        state.currentQuickFilter = payload.currentQuickFilter
        state.appliedFilters = payload.appliedFilters
        state.pagination = payload.pagination
    },


    [VUEX_CALENDAR_ITEM_GET_ATTACHMENTS_SUCCESS]: (state, payload) => {
        if (payload.length > 0) 
            state.calendarItemAttachments = payload
    },

    [VUEX_CALENDAR_ATTACH_FILE_SUCCESS]: (state, payload) => {
        state.calendarItemAttachments.unshift(payload)
    },

    [VUEX_ABSENCES_SET_SIDEBAR_MAINTAINER]: (state, payload) => {
        state.toanSidebarMaintainer = payload
    },


}

/**
 * The CalendarItems module of our Vuex store.
 * @module store/modules/calendarItems
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
